import { OperatingHourCreateRequest } from '~types';

/**
 *
 * addressInfo.state will take short_name instead of long_name (US instead of United States)
 */
export const parseGooglePlaceInfoForCreateAddress = (
  addressComponents?: google.maps.GeocoderAddressComponent[],
) => {
  const addressInfo = {
    state: '',
    city: '',
    zip: '',
    country: '',
    countryCode: '',
  };

  if (!addressComponents) return addressInfo;

  addressComponents.forEach(({ types, long_name, short_name }) => {
    switch (true) {
      case types.includes('locality'):
        addressInfo.city = long_name;
        break;
      case types.includes('administrative_area_level_1'):
        addressInfo.state = long_name;
        // Use state name as fallback for city if city is empty
        addressInfo.city ||= long_name;
        break;
      case types.includes('country'):
        addressInfo.country = long_name;
        addressInfo.countryCode = short_name;
        break;
      case types.includes('postal_code'):
        addressInfo.zip = long_name;
        break;
    }
  });

  return addressInfo;
};

export const mapGooglePeriodsToOperatingHours = (
  periods: google.maps.places.PlaceOpeningHoursPeriod[] | null | undefined,
): OperatingHourCreateRequest[] | undefined => {
  if (!periods) return undefined;

  return periods.flatMap((p) =>
    toListOperatingHourCreateRequest({
      open: { day: p.open.day, time: p.open.time },
      close: p.close
        ? { day: p.close.day, time: p.close.time }
        : { day: p.open.day, time: '2359' }, // there can be cases where closing time does not exist (like 24/7 service) --> this is a fallback for such cases
    }),
  );
};

const dayOfWeekMap: Record<number, string> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

type GoogleOperatingHourPeriod = {
  open: { day: number; time: string };
  close: { day: number; time: string };
};

const formatTime = (time: string): string =>
  `${time.substring(0, 2)}:${time.substring(2, 4)}`;

export function toListOperatingHourCreateRequest(
  period: GoogleOperatingHourPeriod,
): OperatingHourCreateRequest[] {
  const { open, close } = period;

  // Regular case: same-day opening & closing
  if (open.day === close.day) {
    return [
      {
        dayOfWeek: dayOfWeekMap[open.day],
        openTime: formatTime(open.time),
        closeTime: formatTime(close.time),
      },
    ];
  }

  const result: OperatingHourCreateRequest[] = [];

  // Cross-day case
  // First entry for opening day (until 23:59)
  result.push({
    dayOfWeek: dayOfWeekMap[open.day],
    openTime: formatTime(open.time),
    closeTime: '23:59',
  });

  // Fill in the middle days (if there's any)
  for (const day of getDaysBetween(open.day, close.day)) {
    result.push({
      dayOfWeek: dayOfWeekMap[day],
      openTime: '00:00',
      closeTime: '23:59',
    });
  }

  // Last entry for closing day (from 00:00 to close time)
  result.push({
    dayOfWeek: dayOfWeekMap[close.day],
    openTime: '00:00',
    closeTime: formatTime(close.time),
  });

  return result;
}

export function getDaysBetween(startDay: number, endDay: number): number[] {
  const days: number[] = [];
  let currentDay = (startDay + 1) % 7;

  while (currentDay !== endDay) {
    days.push(currentDay);
    currentDay = (currentDay + 1) % 7;
  }

  return days;
}
